/**
 * AppProviders — every Provider that wraps the whole app, in one place.
 *
 * Two providers, both pure infrastructure:
 *   PersistQueryClientProvider   TanStack cache + localStorage hydrate
 *   BrowserRouter                routing
 *
 * Why PersistQueryClientProvider instead of plain QueryClientProvider:
 * the persister hydrates the cache from localStorage *synchronously*
 * before the first render. Effect: on a fresh page load, the dashboard
 * tiles show last-session's "X comments this term" count immediately
 * — no loading-state flicker, no content layout shift. TanStack still
 * refetches in the background to refresh stale data; the UI doesn't
 * shift while that happens because the placeholder was already showing
 * real (if slightly stale) numbers.
 *
 * AuthProvider is gone. Teacher state lives in the TanStack cache
 * (useTeacher()); auth-flow methods are plain hooks under auth/
 * (useLogin, useLogout, startGoogleSignIn).
 */
import { ReactNode } from "react";
import { BrowserRouter } from "react-router-dom";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { queryClient, queryPersistOptions } from "./api/queryClient";

export default function AppProviders({ children }: { children: ReactNode }) {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={queryPersistOptions}
    >
      <BrowserRouter>{children}</BrowserRouter>
    </PersistQueryClientProvider>
  );
}
